import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { calcSampleKPS } from "./utils";
import { sampleKeywordPresets } from "./constants";
import type { ScoringWeights, KeywordSalesTier, HeroRankTier, TrendTier, SampleKeywordPreset, FactorKey } from "./types";
import type { DistributionItem } from "./ScoringWeightsCard";

interface KPSPreviewCardProps {
  weights: ScoringWeights;
  asinDensityMax: number;
  competitionMax: number;
  keywordSalesTiers: KeywordSalesTier[];
  heroRankTiers: HeroRankTier[];
  trendTiers: TrendTier[];
  distributionItems: DistributionItem[];
  factorEnabled: Record<FactorKey, boolean>;
}

const METRIC_KEY_MAP: Record<string, string> = {
  "kps-module-relevance": "relevance",
  "kps-module-search-volume": "searchVolume",
  "kps-module-hero-rank": "heroRank",
  "kps-module-asin-density": "asinDensity",
  "kps-module-keyword-sales": "keywordSales",
  "kps-module-competition": "competition",
  "kps-module-search-trend": "searchTrend",
};

function getMetricByKey(keyword: SampleKeywordPreset): Record<string, { label: string; value: string }> {
  return {
    relevance: { label: "Relevance", value: `${keyword.relevance}%` },
    searchVolume: { label: "Search Vol", value: keyword.searchVolume.toLocaleString() },
    heroRank: { label: "Hero Rank", value: `#${keyword.heroRank}` },
    asinDensity: { label: "ASIN Density", value: `${keyword.asinDensity}%` },
    keywordSales: { label: "Keyword Sales", value: `$${keyword.keywordSales.toLocaleString()}` },
    competition: { label: "Competition", value: keyword.competitors.toLocaleString() },
    searchTrend: { label: "Search Trend", value: `+${keyword.searchTrend}%` },
  };
}

function getScoreByModuleId(kps: ReturnType<typeof calcSampleKPS>): Record<string, number> {
  return {
    "kps-module-relevance": kps.relScore,
    "kps-module-search-volume": kps.svScore,
    "kps-module-hero-rank": kps.hrScore,
    "kps-module-asin-density": kps.adScore,
    "kps-module-keyword-sales": kps.ksScore,
    "kps-module-competition": kps.compScore,
    "kps-module-search-trend": kps.svtScore,
  };
}

export function KPSPreviewCard({
  weights,
  asinDensityMax,
  competitionMax,
  keywordSalesTiers,
  heroRankTiers,
  trendTiers,
  distributionItems,
  factorEnabled,
}: KPSPreviewCardProps) {
  const [sampleKeyword, setSampleKeyword] = useState<SampleKeywordPreset>(sampleKeywordPresets[0]);

  const sampleKPS = calcSampleKPS(weights, sampleKeyword, asinDensityMax, keywordSalesTiers, heroRankTiers, trendTiers, competitionMax, factorEnabled);
  const metricByKey = getMetricByKey(sampleKeyword);
  const scoreByModuleId = getScoreByModuleId(sampleKPS);

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle className="text-base">Live KPS Preview</CardTitle>
        <CardDescription>See how a sample keyword scores with your current configuration</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Choose a keyword</Label>
          <Select
            value={sampleKeyword.keyword}
            onValueChange={(val) => {
              const preset = sampleKeywordPresets.find((p) => p.keyword === val);
              if (preset) setSampleKeyword(preset);
            }}
          >
            <SelectTrigger className="h-9 rounded-lg bg-card text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sampleKeywordPresets.map((preset) => (
                <SelectItem key={preset.keyword} value={preset.keyword}>
                  {preset.keyword}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {distributionItems.map((item) => {
            const m = metricByKey[METRIC_KEY_MAP[item.moduleId]];
            return (
              <Card key={item.moduleId} variant="default">
                <CardContent className="p-3 space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <div className={cn("h-2 w-2 rounded-full shrink-0", item.color)} />
                    <p className="text-xs text-muted-foreground">{m.label}</p>
                  </div>
                  <p className="text-sm font-semibold text-foreground tabular-nums">{m.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card variant="default">
          <CardContent className="p-4 text-center space-y-1">
            <p className="text-xs text-muted-foreground">KPS Score</p>
            <p className="text-3xl font-bold tabular-nums text-foreground">{sampleKPS.total}</p>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-foreground">Score Breakdown</p>
          <div className="space-y-1.5">
            {distributionItems.map((item) => {
              const score = scoreByModuleId[item.moduleId];
              return (
                <div key={item.moduleId} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className={cn("h-2 w-2 rounded-full", item.color)} />
                    <span className="text-muted-foreground">{item.label}</span>
                  </div>
                  <span className="tabular-nums font-medium text-foreground">{score}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
